import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { EvidenceDocumentsStyled } from '../../asset/styled/EvidenceDocumentsStyled';

import titleImg from '../../asset/img/evidence_title_img.svg';
import InfoBanner from '../../components/Banner';
import SubTitleSection from '../../components/SubTitleSection';
import AddButton from './AddButton';
import Typography from '../../components/common/Typography';
import EvidenceFileCard from './EvidenceFileCard';
import EvidenceTypeModal from './EvidenceTypeModal';
import SlideDialogV from '../../components/common/SlideDialogV';
import {
  getNoPaperCategoryLoad,
  getNopaperDetail,
  getNopaperNew,
  resetEvidenceDocumentsState,
  uploadEvidenceDocuments,
  downloadEvidenceDocument,
} from './evidenceDocumentsSlice';
import { deleteEvidenceDocuments, fetchDetails, groupDataByPerson } from './util';
import FileInput from '../../components/common/FileInput';
import { FILE_MAP, RELATION_MAP } from '../../common/data';
import Viewer from './Viewer';
import { getAlertMessageFunction, magamCertifyCheck } from '../../common/Util';
import NotDocument from './NotDocument';

const EvidenceDocuments = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const fileInputRef2 = useRef(null);
  const { t } = useTranslation('evidenceDocuments');
  const { t: tCommon } = useTranslation('common');
  const { originList, reload, detailList, isRender } = useSelector(
    state => state.evidenceDocuments,
  );
  const { isMagam, isCertify } = useSelector(state => state.common);

  //증빙서류 모달에서 type 선택했을때
  const [selectedCategory, setSelectedCategory] = useState('');

  //증빙서류  +버튼 클릭 했을때
  const [selectedDocument, setSelectedDocument] = useState('');

  //실제 데이터 뿌리는용도
  const [dataList, setDataList] = useState([]);
  //증빙서류
  const [viewFile, setViewFile] = useState(null);
  //파일 타입 저장 (이미지/PDF 구분용)
  const [viewFileType, setViewFileType] = useState('');

  const relation = data => (RELATION_MAP[data] ? tCommon(RELATION_MAP[data]) : '');
  const fileNm = data => (FILE_MAP[data] ? tCommon(FILE_MAP[data]) : '');

  //증빙서류 메인 불러오기(필요한 증빙서류 목록들)
  useEffect(() => {
    if (reload == -1) {
      dispatch(getNopaperNew());
    }
    //초기화함수
    return () => {
      dispatch(resetEvidenceDocumentsState());
    };
  }, []);

  useEffect(() => {
    if (reload != -1) {
      dispatch(getNopaperNew());
    }
  }, [reload]);

  //증빙서류 상세 불러오기(업로드 된 증빙서류 목록들)
  useEffect(() => {
    // fetchDetails(dispatch, originList, setDataList);
    if (originList.length > 0) {
      dispatch(getNopaperDetail());
    }
  }, [originList, dispatch]);

  //증빙 메인이랑 상세 데이터 매칭
  useEffect(() => {
    // if (detailList.length > 0) {
    fetchDetails(originList, detailList, setDataList);
    // }
  }, [detailList]);

  //증빙서류 선택모달 열릴때
  useEffect(() => {
    if (selectedDocument?.htmItemType) {
      dispatch(getNoPaperCategoryLoad({ htmItemType: selectedDocument.htmItemType }));
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    // 컴포넌트 언마운트 시 스크롤 상태 복구
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedDocument, dispatch]);

  //증빙서류를 사람별로 그룹화 하는 함수
  const groupedDataList = useMemo(() => groupDataByPerson(dataList), [dataList]);

  const handleCategory = category => {
    setSelectedCategory(category);
    // setSelectedDocument('');
  };

  const clickUploadButton = async e => {
    const alertMessage = getAlertMessageFunction();
    let formData = new FormData();
    let fileObj = e.target.files[0];

    const { htmItemType, htmInsaPerNm } = selectedDocument;
    const { htmItemSeq } = selectedCategory;

    if (!htmItemType && !htmItemSeq) {
      return;
    }

    formData.append('files', fileObj);
    formData.append('htmItemType', htmItemType);
    formData.append('htmItemSeq', htmItemSeq);
    formData.append('htmInsaPerNm', htmInsaPerNm);

    try {
      const response = await dispatch(uploadEvidenceDocuments(formData)).unwrap();
      const { deType, sessionMsg, sessionCode } = response;
      if (deType) {
        alertMessage(deType);
        return;
      }

      if (sessionCode == '-3' && sessionMsg) {
        alertMessage(sessionMsg);
      }

      setSelectedDocument('');
      setSelectedCategory('');
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleDeleteEvidenceDocuments = file => {
    if (!magamCertifyCheck(isMagam, isCertify)) {
      return;
    }

    let deleteData = {
      htmFileSeq: file.htmFileSeq,
      htmInsaPerNm: file.htmInsaPerNm,
      htmItemType: file.htmItemType,
      htmCertifyDate: '',
      htmFileUpNm: file.htmFileUpNm,
    };

    deleteEvidenceDocuments(dispatch, tCommon, { htmDetailFlag: 'Y', formData: [deleteData] });
  };

  const handleViewFile = async file => {
    const alertMessage = getAlertMessageFunction();
    let fileType = file.htmFileUpNm.split('.').pop().toLowerCase();

    try {
      // 이전 blob URL이 있으면 먼저 해제
      if (viewFile) {
        URL.revokeObjectURL(viewFile);
      }

      // download API 호출
      const blob = await dispatch(
        downloadEvidenceDocument({
          htmFileSeq: file.htmFileSeq,
          htmFileType: 'nopaper',
        }),
      ).unwrap();

      // blob URL 생성
      const blobUrl = URL.createObjectURL(blob);

      // 파일 타입과 URL 저장
      setViewFileType(fileType);
      setViewFile(blobUrl);
    } catch (error) {
      console.error('파일 로드 실패:', error);
      alertMessage('파일을 불러올 수 없습니다.');
    }
  };

  // Viewer 닫을 때 blob URL 정리
  const handleCloseViewer = () => {
    if (viewFile && typeof viewFile === 'string') {
      URL.revokeObjectURL(viewFile); // 메모리 해제
    }
    setViewFile(null);
    setViewFileType('');
  };

  // 컴포넌트 언마운트 시 blob URL cleanup
  useEffect(() => {
    return () => {
      if (viewFile && typeof viewFile === 'string') {
        URL.revokeObjectURL(viewFile);
      }
    };
  }, [viewFile]);

  if (!isRender) {
    return null;
  }

  if (originList.length === 0) {
    return <NotDocument />;
  }

  return (
    <EvidenceDocumentsStyled>
      <InfoBanner title={t('bannerSection.title')} description={t('bannerSection.description')} />
      <SubTitleSection title={t('title')} img={titleImg} />

      {/* 인원별 카드 */}
      <div>
        {groupedDataList.map((person, personIndex) => (
          <div className="cardContainer" key={personIndex}>
            <Typography variant="body1Bold">
              {person.htmInsaPerNm}
              {/* 관계안줌 */}
              {/* ({relation(person.relation)}) */}
            </Typography>
            {Object.values(person.documents).map(document => (
              <Fragment key={document.htmItemType}>
                <AddButton
                  title={document.htmItemTypeName || document.htmItemType}
                  status={document.files.length > 0}
                  onClick={() => {
                    if (!magamCertifyCheck(isMagam, isCertify)) {
                      return;
                    }

                    setSelectedDocument({ ...document, htmInsaPerNm: person.htmInsaPerNm });
                  }}
                />
                {document.files.length > 0 && (
                  <div className="displayFlexColumn" style={{ gap: '10px', paddingLeft: '24px' }}>
                    {document.files.map((file, fileIndex) => (
                      <EvidenceFileCard
                        key={fileIndex}
                        fileName={file.htmItemSeqName}
                        fileSize={file.htmFileSize}
                        htmRegDate={file.htmEditDate}
                        fileType={file.htmFileUpNm.split('.').pop()}
                        onDelete={() => handleDeleteEvidenceDocuments(file)}
                        onClick={() => handleViewFile(file)}
                      />
                    ))}
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        ))}
      </div>

      {/* 증빙서류 선택모달 */}
      {selectedDocument?.htmItemType && (
        <EvidenceTypeModal
          handleCategory={handleCategory}
          onClose={() => setSelectedDocument('')}
        />
      )}

      {/* 증빙서류 보는 모달 */}
      <Viewer
        isOpen={!!viewFile}
        onClose={handleCloseViewer}
        file={viewFile}
        fileType={viewFileType}
      />

      <SlideDialogV
        isOpen={selectedCategory != ''}
        title={t('slideDialogTitle')}
        description={t('slideDialogDescription')}
        buttons={[
          {
            label: '이미지파일',
            onClick: () => {
              fileInputRef.current.click();
            },
            color: 'sub',
          },
          {
            label: 'PDF파일',
            onClick: () => {
              fileInputRef2.current.click();
            },
            color: 'sub',
          },
        ]}
        onClose={() => {
          setSelectedCategory('');
          setSelectedDocument('');
        }}
      />
      <FileInput
        fileRef={fileInputRef}
        fileHandler={clickUploadButton}
        multiple={false}
        accept="image/*"
      />
      <FileInput
        fileRef={fileInputRef2}
        fileHandler={clickUploadButton}
        multiple={false}
        accept="application/pdf"
      />
    </EvidenceDocumentsStyled>
  );
};

export default EvidenceDocuments;
